import { Box, Checkbox, createStyles, Group, Stack } from '@mantine/core';
import type { MonitorNotificationTypes } from '@repo/api-codegen';
import { SettingsBlock, SettingsButton } from '@repo/common/components';
import { observer } from 'mobx-react-lite';

interface MonitorNotificationEventsProps {
	value?: MonitorNotificationTypes;
	onChange: (value: MonitorNotificationTypes) => void;
}

const NOTIFICATION_EVENTS_CONFIG = [
	{
		key: 'incident_create',
		label: 'Incident created',
		defaultValue: true,
	},
	{
		key: 'incident_acknowledge',
		label: 'Incident acknowledged',
		defaultValue: true,
	},
	{
		key: 'incident_resolve',
		label: 'Incident resolved',
		defaultValue: true,
	},
	{
		key: 'incident_normal',
		label: 'Monitor back to normal',
		defaultValue: true,
	},
] as const;

const DEFAULT_NOTIFICATION_EVENTS = NOTIFICATION_EVENTS_CONFIG.reduce(
	(acc, config) => ({
		...acc,
		[config.key]: config.defaultValue,
	}),
	{} as MonitorNotificationTypes
);

const useStyles = createStyles((theme) => ({
	checkboxItem: {
		backgroundColor: 'transparent',
		borderRadius: theme.radius.sm,
	},
}));

export const MonitorNotificationEvents = observer(
	({
		value = DEFAULT_NOTIFICATION_EVENTS,
		onChange,
	}: MonitorNotificationEventsProps) => {
		const { classes } = useStyles();

		const handleToggle = (key: keyof MonitorNotificationTypes) => {
			onChange({
				...value,
				[key]: !value[key],
			});
		};

		return (
			<Box mb="4xl">
				<SettingsButton
					variant="tertiary"
					title="Notification Events"
					tooltip="Notification events settings"
					aria-label="Notification events settings"
					description="Choose which monitor events should trigger notifications"
				/>

				<SettingsBlock
					title=""
					description={
						<Stack spacing="sm">
							{NOTIFICATION_EVENTS_CONFIG.map((config) => (
								<Group
									key={config.key}
									position="apart"
									className={classes.checkboxItem}
								>
									<Checkbox
										label={config.label}
										checked={value[config.key]}
										onChange={() => handleToggle(config.key)}
									/>
								</Group>
							))}
						</Stack>
					}
				/>
			</Box>
		);
	}
);
